/**
 * Export and Interaction Utilities
 *
 * This file contains utility functions for SVG export and user interactions
 * extracted from MyCanvas.js to improve code organization and maintainability.
 */

/**
 * Rasterize SVG to data URL
 * @param {string} svgStringExport - SVG string to rasterize
 * @param {string} mime - MIME type for output (default: "image/png")
 * @returns {Promise} Promise that resolves with dataUrl, width, and height
 */
export const createRasterizeSvg =
  () =>
  (svgStringExport, mime = "image/png") =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const svgBlob = new Blob([svgStringExport], { type: "image/svg+xml" });
      const url = URL.createObjectURL(svgBlob);
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        try {
          const dataUrl = canvas.toDataURL(mime);

          resolve({ dataUrl, width: img.width, height: img.height });
        } catch (err) {
          // canvas is tainted – propagate so we fall back to html2canvas
          reject(err);
        }
      };
      img.onerror = (err) => {
        URL.revokeObjectURL(url);
        reject(err);
      };
      img.src = url;
    });

/**
 * Restore transformer state
 * @param {Object} transformer - Transformer object
 * @param {Array} currentNodes - Current nodes to restore
 * @param {boolean} transformerExists - Whether transformer exists
 */
export const createRestoreTransformer =
  () => (transformer, currentNodes, transformerExists) => {
    if (transformerExists) {
      transformer.nodes(currentNodes); // Restore
      transformer.getLayer()?.batchDraw();
    }
  };

/**
 * Handle double click on arrow to add waypoint
 * @param {Event} e - Double click event
 * @param {string} arrowId - ID of the arrow
 * @param {Array} points - Current arrow points
 * @param {Function} setArrows - Setter for arrows array
 * @param {Function} setDoubleSidedArrows - Setter for double-sided arrows array
 */
export const createOnDoubleClick =
  (setArrows, setDoubleSidedArrows) => (e, arrowId, points) => {
    const { x, y } = e.target.getStage().getPointerPosition();

    // Find the closest segment (pair of consecutive points)
    let closestIndex = 0;
    let minDist = Infinity;

    for (let i = 0; i < points.length - 2; i += 2) {
      const x1 = points[i],
        y1 = points[i + 1];
      const x2 = points[i + 2],
        y2 = points[i + 3];

      // Calculate midpoint of this segment
      const midX = (x1 + x2) / 2;
      const midY = (y1 + y2) / 2;

      // Distance from clicked position to this midpoint
      const dist = Math.sqrt((x - midX) ** 2 + (y - midY) ** 2);

      if (dist < minDist) {
        minDist = dist;
        closestIndex = i + 2; // Insert before this index
      }
    }

    // Insert new waypoint at the closest segment
    const newPoints = [
      ...points.slice(0, closestIndex), // Keep existing points before the insertion
      x,
      y, // New waypoint
      ...points.slice(closestIndex), // Keep existing points after the insertion
    ];

    // Update the arrow
    setArrows((prevArrows) =>
      prevArrows.map((arrow) =>
        arrow.id === arrowId ? { ...arrow, points: newPoints } : arrow
      )
    );

    // Update the arrow
    setDoubleSidedArrows((prevArrows) =>
      prevArrows.map((arrow) =>
        arrow.id === arrowId ? { ...arrow, points: newPoints } : arrow
      )
    );
  };

